import { formatNumber, formatOrdinal, MEASURE_COLOR_LIST } from "../utils/helper"
import type { sampleImages, SamplesResponseType } from "../../context/samplesContext"
import type { MeasurementReportGroup } from "./types"

export const PREVIEW_MAX_WIDTH = 1024
export const DEFAULT_STROKE_COLOR = "#FF6C6C"

export function extractFileNameFromHeader(header: string | null): string | null {
  if (!header) {
    return null
  }
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match && utf8Match[1]) {
    const decodedCandidate = utf8Match[1]
    try {
      return decodeURIComponent(decodedCandidate)
    } catch {
      return decodedCandidate
    }
  }
  const asciiMatch = header.match(/filename="?([^";]+)"?/i)
  if (asciiMatch && asciiMatch[1]) {
    return asciiMatch[1]
  }
  return null
}

export async function buildAnnotatedImage(
  sample: SamplesResponseType,
  getColorFn: (originalIdx: number) => string,
  getDisplayIndexFn: (originalIdx: number) => number,
  baseImageEntry?: sampleImages
): Promise<string | null> {
  const targetImageEntry = baseImageEntry ?? sample.sampleImages?.[0]
  if (!targetImageEntry?.url) {
    return null
  }

  const imageElement = await loadImageElement(targetImageEntry.url)
  const desiredMaxWidth = 930
  const aspectRatio = imageElement.width / Math.max(1, imageElement.height)
  const displayWidth = Math.max(1, Math.min(imageElement.width, desiredMaxWidth))
  const displayHeight = Math.max(1, Math.round(displayWidth / aspectRatio))
  const dpr = window.devicePixelRatio || 1

  const canvas = document.createElement("canvas")
  canvas.width = Math.max(1, Math.floor(displayWidth * dpr))
  canvas.height = Math.max(1, Math.floor(displayHeight * dpr))

  const ctx = canvas.getContext("2d")
  if (!ctx) {
    return null
  }

  if (typeof ctx.resetTransform === "function") {
    ctx.resetTransform()
  }
  ctx.scale(dpr, dpr)
  ctx.lineCap = "round"
  ctx.lineJoin = "round"
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = "high"

  ctx.clearRect(0, 0, displayWidth, displayHeight)
  ctx.drawImage(
    imageElement,
    0,
    0,
    imageElement.width,
    imageElement.height,
    0,
    0,
    displayWidth,
    displayHeight
  )

  const scaleX = displayWidth / Math.max(1, imageElement.width)
  const scaleY = displayHeight / Math.max(1, imageElement.height)

  const sortMeasurements = (measurements: SamplesResponseType["sampleMeasurements"]) => {
    return [...measurements]
    .sort((a, b) => {
      const idxA = typeof a.measure_index === "number" ? a.measure_index : Number.MAX_SAFE_INTEGER
      const idxB = typeof b.measure_index === "number" ? b.measure_index : Number.MAX_SAFE_INTEGER
      if (idxA === idxB) {
        return (a.measure_name || "").localeCompare(b.measure_name || "")
      }
      return idxA - idxB
    })
  }

  const fallbackIndexMap = new Map<SamplesResponseType["sampleMeasurements"][number], number>()
  sortMeasurements(sample.sampleMeasurements)
    .filter(measurement => !measurement.is_deleted)
    .filter(measurement => Array.isArray(measurement.result_point) && measurement.result_point.length >= 2)
    .forEach((measurement, idx) => {
      fallbackIndexMap.set(measurement, idx + 1)
    })

  const activeMeasurements = sortMeasurements(
    sample.sampleMeasurements.filter(measurement => {
      if (measurement.is_deleted) {
        return false
      }
      if (typeof measurement.image_index === "number") {
        return measurement.image_index === targetImageEntry.image_index
      }
      return targetImageEntry.image_index === sample.sampleImages?.[0]?.image_index
    })
  )

  const drawableMeasurements = activeMeasurements
    .filter(measurement => Array.isArray(measurement.result_point) && measurement.result_point.length >= 2)
    .map((measurement, idx) => ({
      measurement,
      fallbackIndex: idx + 1
    }))

  drawableMeasurements.forEach(({ measurement, fallbackIndex }) => {
    const points = measurement.result_point.map(point => ({
      x: point.x * scaleX,
      y: point.y * scaleY
    }))

    if (points.length < 2) {
      return
    }

    const baseIndex =
      typeof measurement.measure_index === "number" && measurement.measure_index > 0
        ? measurement.measure_index
        : fallbackIndexMap.get(measurement) ?? fallbackIndex

    const hasValidMeasureIndex = typeof measurement.measure_index === "number" && measurement.measure_index > 0
    const contextDisplayIndex = hasValidMeasureIndex ? getDisplayIndexFn?.(baseIndex) ?? 0 : 0
    const effectiveDisplayIndex = contextDisplayIndex > 0 ? contextDisplayIndex : baseIndex

    const colorCandidate = hasValidMeasureIndex ? getColorFn?.(baseIndex) : undefined
    const fallbackColor = getStrokeColor(baseIndex)
    const useContextColor = typeof colorCandidate === "string" && colorCandidate !== "black"
    const strokeColor = useContextColor ? colorCandidate : fallbackColor

    ctx.strokeStyle = strokeColor
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(points[0].x, points[0].y)
    points.slice(1).forEach(point => ctx.lineTo(point.x, point.y))
    ctx.stroke()

    const first = points[0]
    const second = points[1]
    const mid = {
      x: (first.x + second.x) / 2,
      y: (first.y + second.y) / 2
    }
    const segPx = Math.hypot(second.x - first.x, second.y - first.y)
    const fontSize = Math.min(20, Math.max(10, segPx * 0.06))
    const radius = fontSize * 0.55

    ctx.beginPath()
    ctx.fillStyle = "#FFFFFF"
    ctx.arc(mid.x, mid.y, radius, 0, Math.PI * 2)
    ctx.fill()

    const labelIndex = effectiveDisplayIndex > 0 ? effectiveDisplayIndex : baseIndex
    const labelText = labelIndex > 0 ? formatNumber(labelIndex) : String(labelIndex)

    ctx.fillStyle = "#000000"
    ctx.font = `${fontSize}px sans-serif`
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillText(labelText, mid.x, mid.y)
  })

  return canvas.toDataURL("image/png")
}

export async function buildAnnotatedImages(
  sample: SamplesResponseType,
  getColorFn: (originalIdx: number) => string,
  getDisplayIndexFn: (originalIdx: number) => number,
  maxCount = 2
): Promise<string[]> {
  const imageEntries = [...(sample.sampleImages ?? [])]
    .filter(image => Boolean(image.url))
    .sort((a, b) => a.image_index - b.image_index)

  const annotatedImages = Array.from({ length: maxCount }, () => "")
  for (const imageEntry of imageEntries) {
    const targetIndex = imageEntry.image_index - 1
    if (targetIndex < 0 || targetIndex >= maxCount) {
      continue
    }
    const annotatedImage = await buildAnnotatedImage(sample, getColorFn, getDisplayIndexFn, imageEntry)
    if (annotatedImage) {
      annotatedImages[targetIndex] = annotatedImage
    }
  }

  while (annotatedImages.length > 0 && annotatedImages[annotatedImages.length - 1] === "") {
    annotatedImages.pop()
  }

  return annotatedImages
}

export function getKindDisplay(item: Pick<MeasurementReportGroup, "has_final" | "representative_kind" | "time_values">) {
  if (item.has_final || item.representative_kind === "final") {
    return "FINAL"
  }
  if (item.time_values.length > 0) {
    return item.time_values.map(value => formatOrdinal(value)).join(", ")
  }
  return "-"
}

function getStrokeColor(measureIndex: number): string {
  const entry = MEASURE_COLOR_LIST.find(item => item.measure_index === measureIndex)
  return entry?.color ?? DEFAULT_STROKE_COLOR
}

function loadImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`))
    img.src = url
  })
}
